/*
 * Collection toolbar: pin below the navbar while scrolling, host category
 * buttons on desktop, apply sort without a full reload, and keep focused
 * controls visible. CSS sticky fails inside theme-editor / overflow ancestors,
 * so the pin uses position:fixed on document.body.
 */
(function () {
  const TOOLBAR_SCRIPT_VERSION = 6;
  if (window.__collectionToolbarVersion >= TOOLBAR_SCRIPT_VERSION) return;
  window.__collectionToolbarVersion = TOOLBAR_SCRIPT_VERSION;
  window.__collectionToolbarBound = true;

  const desktopToolbar = window.matchMedia('(min-width: 1025px)');
  const boundScrollers = new Set();
  let pinFrame = 0;

  const toolbarShell = () => document.querySelector('[data-collection-toolbar-shell]');

  const headerEl = () => {
    const fixedArea = document.querySelector('.page-header_fixed-area');
    if (fixedArea) return fixedArea;
    return document.querySelector('.page-header_box, .header_w, .page-header');
  };

  const gridEl = () =>
    document.querySelector('.main-featured-product-grid [data-render-grid], .main-featured-product-grid .products-grid');

  const shouldStickViewport = () => window.innerWidth > 600 && window.innerHeight > 500;

  const pinnedHeaderOffset = (header) => {
    if (!header) return 0;
    const rect = header.getBoundingClientRect();
    const position = getComputedStyle(header).position;
    if (position === 'fixed' && rect.bottom > 0) return rect.bottom;
    if (rect.top <= 1 && rect.bottom > 0) return rect.bottom;
    return 0;
  };

  const ensureSentinel = (bar) => {
    const existing = document.querySelector('[data-collection-toolbar-sentinel]');
    if (existing) return existing;
    if (!bar || !bar.parentNode) return null;
    const sentinel = document.createElement('div');
    sentinel.setAttribute('data-collection-toolbar-sentinel', '');
    sentinel.setAttribute('aria-hidden', 'true');
    bar.parentNode.insertBefore(sentinel, bar);
    return sentinel;
  };

  const restoreToolbar = (bar, sentinel) => {
    if (!bar) return;
    bar.classList.remove('is-stuck');
    bar.style.removeProperty('position');
    bar.style.removeProperty('top');
    bar.style.removeProperty('left');
    bar.style.removeProperty('width');
    bar.style.removeProperty('right');
    bar.style.removeProperty('z-index');
    if (sentinel) {
      sentinel.style.height = '0px';
      if (bar.previousElementSibling !== sentinel) {
        sentinel.after(bar);
      }
    }
    document.documentElement.style.setProperty('--collection-header-height', '0px');
    document.documentElement.style.setProperty('--collection-toolbar-height', '0px');
    document.documentElement.style.removeProperty('--collection-fixed-header-bottom');
  };

  const pinToolbar = () => {
    const bar = toolbarShell();
    if (!bar) return;

    const sentinel = ensureSentinel(bar);
    const header = headerEl();
    const headerBottom = pinnedHeaderOffset(header);

    if (!shouldStickViewport()) {
      restoreToolbar(bar, sentinel);
      return;
    }

    const marker = sentinel || bar;
    const sentinelTop = marker.getBoundingClientRect().top;
    const gridBottom = gridEl()?.getBoundingClientRect().bottom;
    const pastGrid = gridBottom != null && gridBottom <= headerBottom;

    if (sentinelTop > headerBottom + 0.5 || pastGrid) {
      restoreToolbar(bar, sentinel);
      return;
    }

    const width = (sentinel || bar).getBoundingClientRect().width;
    const left = (sentinel || bar).getBoundingClientRect().left;
    const height = bar.offsetHeight;

    if (sentinel) sentinel.style.height = `${height}px`;
    if (bar.parentElement !== document.body) {
      document.body.appendChild(bar);
    }

    bar.classList.add('is-stuck');
    bar.style.position = 'fixed';
    bar.style.top = `${headerBottom}px`;
    bar.style.left = `${left}px`;
    bar.style.width = `${width}px`;
    bar.style.zIndex = '89990';

    document.documentElement.style.setProperty('--collection-fixed-header-bottom', `${headerBottom}px`);
    document.documentElement.style.setProperty('--collection-header-height', `${headerBottom}px`);
    document.documentElement.style.setProperty('--collection-toolbar-height', `${height}px`);
  };

  const schedulePin = () => {
    if (pinFrame) return;
    pinFrame = requestAnimationFrame(() => {
      pinFrame = 0;
      pinToolbar();
    });
  };

  const bindScrollTargets = (el) => {
    const targets = [window, document];
    let node = el && el.parentElement;
    while (node && node !== document.documentElement) {
      const style = getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(style.overflowY) || /(auto|scroll|overlay)/.test(style.overflow)) {
        targets.push(node);
      }
      node = node.parentElement;
    }
    targets.forEach((target) => {
      if (boundScrollers.has(target)) return;
      boundScrollers.add(target);
      target.addEventListener('scroll', schedulePin, { passive: true });
    });
  };

  const placeCategoryNavigation = () => {
    const nav = document.querySelector('[data-collection-category-nav]');
    const slot = document.querySelector('[data-collection-category-slot]');
    const home = document.querySelector('[data-collection-category-home]');
    if (!nav || !slot || !home) return;

    const destination = desktopToolbar.matches ? slot : home;
    if (nav.parentElement === destination) return;

    const focused = document.activeElement;
    const restoreFocus = nav.contains(focused);
    destination.appendChild(nav);
    if (restoreFocus) focused.focus({ preventScroll: true });
  };

  const syncSortSelects = (value) => {
    document.querySelectorAll('[data-collection-sort]').forEach((select) => {
      if (select.value !== value) select.value = value;
    });
  };

  const syncPromoTile = (value) => {
    const featured = toolbarShell()?.dataset.featuredSort;
    document.querySelectorAll('[data-collection-promo]').forEach((tile) => {
      tile.hidden = value !== featured;
    });
  };

  const applySort = (value) => {
    const input = document.getElementById('filters-params');
    if (!input || !value) return;

    const url = new URL(input.value, window.location.origin);
    url.searchParams.set('sort_by', value);
    input.value = url.toString();

    syncSortSelects(value);
    syncPromoTile(value);
    document.querySelector('facets-filter')?.onSubmitHandler();
    document.dispatchEvent(new CustomEvent('collection:sort-changed', { detail: { sort: value } }));
  };

  // Chips wrapping, a re-render or the header changing height must update the pin and
  // the scroll padding straight away, not on the next scroll.
  const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => schedulePin()) : null;
  const observeSizes = (bar) => {
    if (!resizeObserver) return;
    resizeObserver.disconnect();
    resizeObserver.observe(bar);
    const header = headerEl();
    if (header) resizeObserver.observe(header);
  };

  // Removing a chip, Clear all or the empty state's Clear filters: once the section has
  // re-rendered, focus the Filters button and announce the new result count.
  let afterFilterRemoval = false;
  let liveRegion = null;
  const announce = (text) => {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.className = 'collection-toolbar__status';
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      liveRegion.textContent = text;
    });
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-collection-chip-remove], [data-collection-clear-all], [data-collection-clear-filters]')) {
      afterFilterRemoval = true;
    }
  });

  const finishFilterRemoval = () => {
    if (!afterFilterRemoval) return;
    afterFilterRemoval = false;
    document
      .querySelector('[data-filter-drawer-open], .collection-toolbar__filters')
      ?.focus({ preventScroll: true });
    const count = document.querySelector('[data-render-result-count]')?.textContent.trim();
    if (count) announce(count);
  };

  const initCollectionToolbar = () => {
    const shells = [...document.querySelectorAll('[data-collection-toolbar-shell]')];
    const inSection =
      shells.find((node) => node.closest('.main-featured-product-grid')) || shells[0];
    shells.forEach((node) => {
      if (node !== inSection) node.remove();
    });
    document.querySelectorAll('[data-collection-toolbar-sentinel]').forEach((node, index) => {
      if (index > 0) node.remove();
    });

    const bar = toolbarShell();
    if (!bar) return;
    placeCategoryNavigation();
    bindScrollTargets(bar);
    observeSizes(bar);
    pinToolbar();
    finishFilterRemoval();
    syncPromoTile(document.querySelector('[data-collection-sort]')?.value);
  };

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-collection-sort]');
    if (!select) return;
    applySort(select.value);
  });

  document.addEventListener('focusin', (event) => {
    if (event.target.closest('dialog, .header_w, .header, .page-header, [data-collection-toolbar-shell]')) {
      return;
    }

    requestAnimationFrame(() => {
      const bar = toolbarShell();
      if (!bar || !bar.classList.contains('is-stuck')) return;

      const rect = event.target.getBoundingClientRect();
      const edge = bar.getBoundingClientRect().bottom;
      if (rect.height > 0 && rect.top < edge + 8 && window.scrollY > 0) {
        window.scrollBy({ top: rect.top - edge - 12, behavior: 'instant' });
      }
    });
  });

  desktopToolbar.addEventListener('change', () => {
    placeCategoryNavigation();
    schedulePin();
  });
  window.addEventListener('resize', schedulePin);

  document.addEventListener('shopify:section:load', initCollectionToolbar);
  document.addEventListener('shopify:section:reorder', initCollectionToolbar);
  document.addEventListener('shopify:section:select', initCollectionToolbar);

  window.initCollectionToolbar = initCollectionToolbar;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionToolbar);
  } else {
    initCollectionToolbar();
  }
})();
